/**
 * Global API Call Manager
 * Prevents duplicate API calls across all components
 * Works with React StrictMode and handles race conditions
 */

class ApiCallManager {
  private activeCalls: Map<string, boolean>;
  private callPromises: Map<string, Promise<unknown>>;

  constructor() {
    this.activeCalls = new Map();
    this.callPromises = new Map();
  }

  /**
   * Execute an API call with duplicate prevention
   */
  async execute<T = unknown>(
    callId: string,
    apiFunction: (...args: unknown[]) => Promise<T>,
    forceRefresh: boolean = false,
    ...args: unknown[]
  ): Promise<T> {
    // If call is already active, return the existing promise
    if (this.activeCalls.has(callId)) {
      console.log(`API Call Manager: Call '${callId}' already in progress, returning existing promise`);
      return this.callPromises.get(callId) as Promise<T>;
    }

    // If we have a cached promise and not forcing refresh, return it
    if (this.callPromises.has(callId) && !forceRefresh) {
      console.log(`API Call Manager: Call '${callId}' has cached promise, returning cached result`);
      return this.callPromises.get(callId) as Promise<T>;
    }

    // If forcing refresh, clear the cache for this call
    if (forceRefresh) {
      console.log(`API Call Manager: Force refresh requested for '${callId}', clearing cache`);
      this.callPromises.delete(callId);
    }

    try {
      console.log(`API Call Manager: Starting call '${callId}'`);
      this.activeCalls.set(callId, true);

      // Create the promise
      const promise = apiFunction(...args);
      this.callPromises.set(callId, promise as Promise<unknown>);

      // Wait for the promise to resolve
      const result = await promise;

      console.log(`API Call Manager: Call '${callId}' completed successfully`);
      return result;
    } catch (error) {
      console.error(`API Call Manager: Call '${callId}' failed:`, error);
      // Remove from cache on error so it can be retried
      this.callPromises.delete(callId);
      throw error;
    } finally {
      this.activeCalls.delete(callId);
    }
  }

  /**
   * Clear cached results for a specific call
   */
  clearCache(callId: string): void {
    console.log(`API Call Manager: Clearing cache for '${callId}'`);
    this.activeCalls.delete(callId);
    this.callPromises.delete(callId);
  }

  /**
   * Clear all cached results
   */
  clearAllCache(): void {
    console.log(`API Call Manager: Clearing all cache`);
    this.activeCalls.clear();
    this.callPromises.clear();
  }

  /**
   * Check if a call is currently active
   */
  isActive(callId: string): boolean {
    return this.activeCalls.has(callId);
  }

  /**
   * Get the status of all calls
   */
  getStatus(): { activeCalls: string[]; cachedCalls: string[] } {
    return {
      activeCalls: Array.from(this.activeCalls.keys()),
      cachedCalls: Array.from(this.callPromises.keys())
    };
  }
}

// Create a singleton instance
const apiCallManager = new ApiCallManager();

export default apiCallManager;
